import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import "@/App.css";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Login from "@/pages/Login";
import AdminShell from "@/components/admin/AdminShell";
import Overview from "@/pages/admin/Overview";
import Classes from "@/pages/admin/Classes";
import ClassReport from "@/pages/admin/ClassReport";
import StudentsPage from "@/pages/admin/Students";
import Templates from "@/pages/admin/Templates";
import Analytics from "@/pages/admin/Analytics";
import Branding from "@/pages/admin/Branding";
import SettingsPage from "@/pages/admin/SettingsPage";
import Join from "@/pages/Join";
import Meeting from "@/pages/Meeting";
import { MeetingErrorBoundary } from "@/components/MeetingErrorBoundary";
import RecordView from "@/pages/RecordView";
import Invite from "@/pages/Invite";
import Goodbye from "@/pages/Goodbye";
import PayPage from "@/pages/PayPage";
import RegisterPay from "@/pages/RegisterPay";
import NotFound from "@/pages/NotFound";
import AudioTest from "@/pages/AudioTest";
import DesignLab from "@/pages/DesignLab";
import { Toaster } from "sonner";

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0a0a0a] text-neutral-400">
        Loading…
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<NotFound />} />
            <Route path="/login" element={<Login />} />
            <Route
              path="/admin"
              element={
                <Protected>
                  <AdminShell />
                </Protected>
              }
            >
              <Route index element={<Overview />} />
              <Route path="classes" element={<Classes />} />
              <Route path="classes/:roomName" element={<ClassReport />} />
              {/* Legacy deep link kept alive */}
              <Route path="meetings/:roomName" element={<ClassReport />} />
              <Route path="students" element={<StudentsPage />} />
              <Route path="templates" element={<Templates />} />
              <Route path="analytics" element={<Analytics />} />
              <Route path="branding" element={<Branding />} />
              <Route path="settings" element={<SettingsPage />} />
            </Route>
            <Route path="/join/:roomName" element={<Join />} />
            <Route path="/record/:roomName" element={<RecordView />} />
            <Route path="/meet/:roomName" element={<MeetingErrorBoundary><Meeting /></MeetingErrorBoundary>} />
            {/* Backward-compat aliases — old links shared in WhatsApp groups keep working. */}
            <Route path="/meet/i/:token" element={<Invite />} />
            <Route path="/i/:token" element={<Invite />} />
            <Route path="/m/:roomName" element={<MeetingErrorBoundary><Meeting /></MeetingErrorBoundary>} />
            <Route path="/goodbye" element={<Goodbye />} />
            <Route path="/pay/:id" element={<PayPage />} />
            <Route path="/register/:roomName" element={<RegisterPay />} />
            <Route path="/r/:roomName" element={<RegisterPay />} />
            <Route path="/audio-test" element={<AudioTest />} />
            <Route path="/design-lab" element={<DesignLab />} />
            {/* Short primary invite URL — udyogclasses.com/<token> */}
            <Route path="/:token" element={<Invite />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
        <Toaster
          theme="dark"
          position="top-right"
          richColors
          closeButton
          gap={8}
          toastOptions={{
            // Compact: an in-class error used to eat a third of a phone screen.
            style: { width: "auto", maxWidth: "300px", padding: "9px 12px", fontSize: "12.5px" },
            classNames: { title: "text-[12.5px] leading-snug", description: "text-[11px] leading-snug" },
          }}
        />
      </AuthProvider>
    </div>
  );
}
